import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type {
  BoardFaceLinkCardSurface,
  BoardFaceList,
  CardAgingMode,
  CardMirror,
} from '@molt/shared';
import type { ReactElement } from 'react';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { DragHandle } from '@/lib/list-drag';
import { isOptimisticListId } from '@/lib/lists';
import type { UndoSourceRegistration } from '@/lib/undo';
import type { PowerUpSurfaceSummary } from '@/lib/power-ups';
import type { CardComposerProps } from './CardComposer';
import type { CardSelectionInput } from './CardTile';
import { ListColumn } from './ListColumn';

/**
 * The `<li>` of the list strip — chunk `C2`, agent E (`FE-11.2`,
 * `FR-BOARD-011`). Signatures fixed by `docs/design/c2/C2-BRIEF.md` §6.5.
 *
 * THE ONE PLACE `useSortable` IS CALLED. It exists as its own file because the
 * sortable node and the drag handle are different elements: `setNodeRef` goes
 * on this `<li>`, while `attributes`, `listeners` and `setActivatorNodeRef`
 * travel down to `ListHeader` as a `DragHandle` — `FR-BOARD-011` drags a list
 * by its header, and brief §7.3 explains why the handle is a dedicated control
 * rather than the header container itself (`attributes` carries
 * `role="button"`, and a header container already holds an `<h2>`, a count and
 * a menu trigger — a control containing controls, which is the shape of C1's
 * `<button>`-inside-`<a>` defect).
 *
 * ---------------------------------------------------------------------------
 * WHY `attributes` PASSES STRAIGHT THROUGH NOW
 * ---------------------------------------------------------------------------
 * It did not at first. `DragHandle.attributes` was `Record<string, unknown>`,
 * and dnd-kit types `attributes` as the **interface** `DraggableAttributes` —
 * an interface has no implicit index signature, so the direct assignment fails
 * with TS2322 and this file shipped `{ ...attributes }` to launder it through
 * an anonymous object type. The parent took the report and changed the shared
 * type to `DraggableAttributes` itself at integration, so the spread is gone.
 * The lesson worth keeping: a deliberately loose shared type does not remove a
 * coupling, it just moves the cast into every consumer.
 *
 * ---------------------------------------------------------------------------
 * WHEN THE COLUMN IS NOT DRAGGABLE
 * ---------------------------------------------------------------------------
 * Two cases, and they are disabled the same way but for different reasons:
 *
 *   1. **An optimistically created list** (`isOptimisticListId`, brief §5.2) —
 *      its id is `optimistic:<uuid>` and the server has never seen it.
 *      Dragging it would send a fake id to `PATCH /lists/:id/position`, or send
 *      a real list a fake id as a *neighbour*: a 404 nobody can trace back to a
 *      drag. This is one of the three places brief §5.2 requires the gate.
 *   2. **A viewer who cannot edit.** Not named in §6.5 — `canEdit` gates the
 *      handle, and no handle means no activator and no listeners, so the column
 *      is already un-draggable. `disabled` as well because it also withdraws
 *      the column as a *droppable*, which is the half a missing handle does not
 *      cover. Reported as an addition rather than assumed.
 *
 * ⚠ A disabled sortable stays in `SortableContext`'s `items` and stays in the
 * DOM in payload order. Filtering it out of `items` while still rendering it
 * makes the strategy's rects and the DOM order disagree, which is the same
 * off-by-one `neighboursForMove` exists to avoid.
 *
 * ---------------------------------------------------------------------------
 * THE GAP ZONE (`FR-BOARD-009` AC3), AND WHY IT IS NOT ITS OWN `<li>`
 * ---------------------------------------------------------------------------
 * AC3 opens a composer by double-clicking the gap between two lists. The gap is
 * the strip's own `gap-3` — 12px of nothing — and the cheapest correct hit
 * target is an absolutely positioned overlay over it, anchored to this `<li>`
 * (`relative` here, `-right-3 w-3` there).
 *
 * A real `<li>` between every pair of columns was the alternative and it is
 * worse in two ways that matter: the strip's flex `gap-3` would then apply on
 * **both** sides of it, doubling the inter-column gutter to 24px and silently
 * invalidating `density.ts`'s `SETTLED.columnGap`; and it would put a stream of
 * empty list items inside `<ol id="board-lists">`, which five e2e specs read.
 *
 * ⚠ The zone is `aria-hidden` and mouse-only **by construction**, which
 * `FR-EXP-022` forbids on its own. The keyboard equivalent is `ListHeader`'s
 * `Add a list to the right` menu item, and both call the SAME
 * `onInsertListAfter` — one callback, one composer, one code path. That is what
 * makes the double-click an accelerator rather than the only way in.
 */
export interface SortableListColumnProps {
  list: BoardFaceList;
  cardAgingMode?: CardAgingMode | undefined;
  /**
   * `FR-CARD-025` AC5 / `FE-25.2` — the board's covers setting, pure
   * pass-through, modelled on `cardAgingMode` directly above it.
   *
   * ⚠ **THIS FILE IS THE MIDDLE OF A THREE-FILE CHAIN, AND THAT IS WHY THE
   * RESIDUAL SURVIVED THREE FAN-OUTS.** The chain is `BoardFace` →
   * `SortableListColumn` → `ListColumn` → `SortableCardTile` → `CardTile`.
   * `FE-25.2`'s bullet, `FE-58.1`'s bullet and `FR-CARD-025.spec.ts`'s own
   * `test.fixme` title all say the fix is *"ONE line each in `ListColumn.tsx`
   * and `BoardFace.tsx`"* — naming the two ENDPOINTS and leaving the middle to
   * a file nobody was told they owned. All three are wrong in exactly the way
   * the four pass-through props below record C3 getting wrong: both endpoints
   * would have compiled, both suites would have passed, and no card front in
   * the composed application would ever have seen the setting. `CardTile`
   * defaults it to `true`, so the symptom is a covers-OFF board drawing every
   * cover, with nothing red anywhere.
   *
   * `| undefined` for `exactOptionalPropertyTypes`, and OPTIONAL rather than
   * required although `boardContentsBoardSchema.coversEnabled` is
   * `z.boolean()`: 19 render sites in this file's own suite and 45 in
   * `ListColumn.test.tsx` construct these components with no board at all, and
   * a required prop would break all 64 — suites this chunk must extend and may
   * not weaken. Forwarded as a PLAIN prop below, never a conditional spread.
   */
  coversEnabled?: boolean | undefined;
  /**
   * `FR-CARD-005`'s optional board setting (`docs/notes/card-keys.md`) —
   * `boardContentsBoardSchema.showCardNumbersOnFront`, threaded on
   * `coversEnabled`'s footing. When `true`, `CardTile` draws the card's `ref`
   * (`MG-0001`) muted before the title; a card whose `ref` is `null` (a viewer
   * outside the reference scope) draws nothing.
   */
  showCardRef?: boolean | undefined;
  /** Owner-directed: board's `dangerousComments` threshold; cards at or above it get a red tint. `null`/undefined = off. */
  dangerousComments?: number | null | undefined;
  canEdit: boolean;
  canWatch?: boolean | undefined;
  /**
   * ⚠ `| undefined` is spelled out because `tsconfig` has
   * **`exactOptionalPropertyTypes: true`**: under that flag `p?: T` accepts an
   * absent property but NOT an explicit `undefined`, so a caller holding a
   * `T | undefined` cannot pass it through. §6.4's `ListHeaderProps` and
   * `ListColumnProps` need the same widening for `dragHandle` and this prop —
   * reported to the parent, since those files are agent C's.
   */
  onInsertListAfter?: ((listId: string) => void) | undefined;
  /**
   * ⚠ ADDED beyond brief §6.5, and reported. True when another list follows
   * this one, so the gutter to its right is a gap *between two lists* and
   * carries `FR-BOARD-009` AC3's double-click zone. It cannot be derived from
   * `onInsertListAfter` being absent: the last list must still offer
   * `Add a list to the right` in its menu (it means "append at the far right"),
   * so the callback is present there while the gap zone is not.
   */
  hasGapAfter?: boolean | undefined;
  /*
   * ⚠ **The four props below are pure pass-through, added by the parent at
   * chunk `C3`'s integration, and their absence was a BLOCKING defect.**
   *
   * `C3-BRIEF.md` §6.5 put them on `ListColumnProps` and §6.7 put the state
   * that feeds them in `BoardFace`. This file sits between the two and
   * declared none of them — so `BoardFace` computed four correct values,
   * `ListColumn` declared four correct props, both files' suites passed, and
   * **no card composer and no quick-edit panel was reachable in the composed
   * application at all.** Agent G found it; agent F could not have, and
   * neither could any test either of them was asked to write.
   *
   * This is the fifth seam of exactly this shape in one chunk (see §12.4 and
   * §12.19). The rule it keeps teaching: **a prop that crosses three files
   * must be named in all three, and the brief has to say so** — naming the
   * two endpoints leaves the middle to a file nobody was told they owned.
   *
   * Each is `| undefined` for `exactOptionalPropertyTypes`, and each is
   * forwarded as a **plain prop, never a conditional spread** — the C2 defect
   * that JSX spread attributes bypass excess-property checking, so a spread
   * compiles even against a prop that was never declared.
   */
  /** Which composer in this column is open — `C3-BRIEF.md` §6.5. */
  openComposer?: 'bottom' | 'top' | null | undefined;
  onOpenComposerChange?: ((placement: 'bottom' | 'top' | null) => void) | undefined;
  /** The card quick edit is open on, if it is in this column. */
  quickEditCardId?: string | null | undefined;
  onQuickEditChange?: ((cardId: string | null) => void) | undefined;
  showLabelNames?: boolean | undefined;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onOpenCardDates?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onArchiveCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onToggleCardCompleted?: ((card: BoardFaceList['cards'][number]) => void) | undefined;
  onMoveCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onCopyCard?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onMoveList?: ((list: BoardFaceList) => void) | undefined;
  onCopyList?: ((list: BoardFaceList) => void) | undefined;
  onOpenListOperations?: ((list: BoardFaceList) => void) | undefined;
  recordUndoSource?:
    ((source: UndoSourceRegistration) => Promise<void> | void) | undefined;
  onPasteMany?: CardComposerProps['onPasteMany'];
  collapsed?: boolean | undefined;
  onCollapseChange?: ((listId: string, collapsed: boolean) => void) | undefined;
  highlightedCardId?: string | null | undefined;
  filterActive?: boolean | undefined;
  selectedCardIds?: ReadonlySet<string> | undefined;
  selectionActive?: boolean | undefined;
  onSelectCardGesture?:
    | ((input: CardSelectionInput, card: BoardFaceList['cards'][number]) => void)
    | undefined;
  onOpenBulkCardActions?:
    ((card: BoardFaceList['cards'][number], listId: string) => void) | undefined;
  onRemoveMirror?: ((mirror: CardMirror) => void) | undefined;
  onConvertLinkCard?: ((card: BoardFaceLinkCardSurface) => void) | undefined;
  pendingSyncCardIds?: ReadonlySet<string> | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
}

export function SortableListColumn({
  list,
  cardAgingMode,
  coversEnabled,
  showCardRef,
  dangerousComments,
  canEdit,
  canWatch,
  onInsertListAfter,
  hasGapAfter = false,
  openComposer,
  onOpenComposerChange,
  quickEditCardId,
  onQuickEditChange,
  showLabelNames,
  onOpenCard,
  onOpenCardDates,
  onArchiveCard,
  onToggleCardCompleted,
  onMoveCard,
  onCopyCard,
  onMoveList,
  onCopyList,
  onOpenListOperations,
  recordUndoSource,
  onPasteMany,
  collapsed = false,
  onCollapseChange,
  highlightedCardId,
  filterActive,
  selectedCardIds,
  selectionActive,
  onSelectCardGesture,
  onOpenBulkCardActions,
  onRemoveMirror,
  onConvertLinkCard,
  pendingSyncCardIds,
  powerUpSurfaces,
}: SortableListColumnProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const isPlaceholder = isOptimisticListId(list.id);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: list.id, disabled: isPlaceholder || !canEdit });

  /*
   * ⚠ ONE GATE FOR BOTH AFFORDANCES, and the second half of it is a defect
   * this file would otherwise ship. Brief §5.2 names three places that must
   * consult `isOptimisticListId`; there is a fourth, here. `onInsertListAfter`
   * carries THIS list's id, and `BoardFace` puts it on `POST /lists` as
   * `previousListId` — so a gap zone (or a menu item) on a placeholder column
   * sends an id the server has never seen, which is the same untraceable 404
   * the drag gate exists to prevent, reached through the composer instead of
   * through a drag.
   */
  const canAct = canEdit && !isPlaceholder;

  const dragHandle: DragHandle | undefined = canAct
    ? { setActivatorNodeRef, attributes, listeners, isDragging }
    : undefined;

  const insertListAfter = canAct ? onInsertListAfter : undefined;

  return (
    <li
      ref={setNodeRef}
      style={{
        /*
         * `CSS.Translate`, not `CSS.Transform`: the latter also applies the
         * scaleX/scaleY dnd-kit computes for variable-sized items, and every
         * column here is exactly `w-list` (292px), so a scale can only ever be
         * 1 — or, if a rect is measured mid-layout, a smeared column of text.
         * Translation is the whole of what a horizontal reorder needs.
         */
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      /*
       * `relative` is the gap zone's anchor. The other four classes are C1's
       * `<li>` verbatim and are load-bearing: `max-h-full min-h-0` is what lets
       * a 200-card column scroll its own cards instead of pushing the page over
       * the viewport edge (`BoardFace`'s docstring, `lib/shell-layout.ts`), and
       * the responsive width class is the density budget's desktop column,
       * while mobile keeps one near-full-width list visible.
       *
       * `z-10` while dragging so the moving column passes over its neighbours
       * rather than under them; nothing else changes, because without a
       * `DragOverlay` this element *is* the thing being dragged and dimming it
       * would hide the item the member is looking at.
       */
      className={cn(
        'relative flex max-h-full min-h-0 shrink-0',
        collapsed
          ? 'w-[calc(100vw-1.5rem)] snap-start md:w-14'
          : 'w-[calc(100vw-1.5rem)] snap-start md:w-list',
        isDragging && 'z-10',
      )}
    >
      {/*
        ⏭ FORWARD — `list.colour` is on the payload and is still deliberately
        ignored: a list colour is `FE-11.x`'s surface, and no requirement in
        `C2`'s three items asks for it (C2 brief §2.2).
      */}
      {/*
        ⚠ Plain props, and the parent widened `ListColumnProps` to `| undefined`
        at integration so they could be. This shipped first as two conditional
        JSX spreads — forced by `exactOptionalPropertyTypes: true`, under which
        `dragHandle?: DragHandle` accepts an ABSENT property and refuses an
        explicit `undefined`, and both are legitimately undefined here (no
        handle on a placeholder or a read-only column). **That form was a false
        green**: JSX spread attributes bypass excess-property checking, so it
        compiled while `ListColumnProps` declared neither prop. The drag-handle
        plumbing — without which `FR-BOARD-011` does not exist — was checked by
        nothing, and `tsc` reported success. Do not reintroduce the spread.
      */}
      <ListColumn
        list={list}
        cardAgingMode={cardAgingMode}
        coversEnabled={coversEnabled}
        showCardRef={showCardRef}
        dangerousComments={dangerousComments}
        canEdit={canEdit}
        canWatch={canWatch}
        onInsertListAfter={insertListAfter}
        dragHandle={dragHandle}
        openComposer={openComposer}
        onOpenComposerChange={onOpenComposerChange}
        quickEditCardId={quickEditCardId}
        onQuickEditChange={onQuickEditChange}
        showLabelNames={showLabelNames}
        onOpenCard={onOpenCard}
        onOpenCardDates={onOpenCardDates}
        onArchiveCard={onArchiveCard}
        onToggleCardCompleted={onToggleCardCompleted}
        onMoveCard={onMoveCard}
        onCopyCard={onCopyCard}
        onMoveList={
          canAct && onMoveList !== undefined ? () => onMoveList(list) : undefined
        }
        onCopyList={
          canAct && onCopyList !== undefined ? () => onCopyList(list) : undefined
        }
        onOpenListOperations={canAct ? onOpenListOperations : undefined}
        recordUndoSource={recordUndoSource}
        onPasteMany={onPasteMany}
        collapsed={collapsed}
        onCollapseChange={
          !isPlaceholder && onCollapseChange !== undefined
            ? (nextCollapsed) => onCollapseChange(list.id, nextCollapsed)
            : undefined
        }
        highlightedCardId={highlightedCardId}
        filterActive={filterActive}
        selectedCardIds={selectedCardIds}
        selectionActive={selectionActive}
        onSelectCardGesture={onSelectCardGesture}
        onOpenBulkCardActions={onOpenBulkCardActions}
        onRemoveMirror={onRemoveMirror}
        onConvertLinkCard={onConvertLinkCard}
        pendingSyncCardIds={pendingSyncCardIds}
        powerUpSurfaces={powerUpSurfaces}
      />

      {hasGapAfter && insertListAfter !== undefined && (
        <div
          aria-hidden="true"
          title={t('boardFace.list.gapInsertTitle')}
          className="absolute inset-y-0 -right-3 w-3 cursor-cell"
          onDoubleClick={() => {
            insertListAfter(list.id);
          }}
        />
      )}
    </li>
  );
}
